import { Modal, Spin, message } from 'antd';
import { createElement } from 'react';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import { downloadKML, estimateKmlBytes } from '../Repository/TreeRepository';
import { megabytes } from './OfflineSaveService';
import {
  KML_SAVED,
  NO_TREES_TO_DOWNLOAD,
  SHORT_MESSAGE_SECONDS,
  TREES_LIST_SIZE_UNREADABLE,
} from '../Helper/BannerMessages';

export interface KmlDownloadProgress {
  /** Bytes read so far, and the size the dialog promised - the API sends the file without a length of its own */
  bytes: number;
  expectedBytes: number;
}

export interface KmlDownloadRequest {
  filter: any;
  admin: boolean;
}

type Listener = (progress: KmlDownloadProgress | null) => void;

const listeners = new Set<Listener>();
let progress: KmlDownloadProgress | null = null;

const report = (next: KmlDownloadProgress | null) => {
  progress = next;
  listeners.forEach((listener) => listener(progress));
};

/** The overlay of a KML download, if one is running (see `DownloadLoader`) */
export function subscribeKmlDownload(listener: Listener): () => void {
  listeners.add(listener);
  listener(progress);

  return () => listeners.delete(listener);
}

/**
 * Asks before the file is built: the question says what the download weighs, worked out from the rows the list
 * counts - the API builds the KML on demand, so its size cannot be read before asking for it.
 */
export async function askAndDownloadKml(request: KmlDownloadRequest): Promise<void> {
  const { filter, admin } = request;

  // The dialog opens at once and works the figure out in place, the same way `Save Offline` asks
  const answer: { bytes: number; trees: number } = { bytes: 0, trees: 0 };
  let cancelled = false;

  const confirm = Modal.confirm({
    title: 'Download KML',
    icon: createElement(Spin, { size: 'small' }),
    content: 'Working out how much there is to download…',
    okText: 'Yes',
    cancelText: 'No',
    okButtonProps: { disabled: true },
    centered: true,
    // The dialog closes on the answer: nothing is awaited here, the overlay behind it reports the download
    onOk: () => {
      if (answer.trees) {
        void startDownload(filter, answer.bytes);
      }
    },
    // Saying No while the size is still being worked out is an answer too: nothing is downloaded
    onCancel: () => {
      cancelled = true;
    },
  });

  try {
    const estimate = await estimateKmlBytes(filter, admin);
    answer.bytes = estimate.bytes;
    answer.trees = estimate.trees;
  } catch (error) {
    console.error('could not work out what the KML download costs', { error });
    confirm.destroy();
    message.error(TREES_LIST_SIZE_UNREADABLE);
    return;
  }

  if (cancelled) {
    return;
  }

  if (!answer.trees) {
    confirm.destroy();
    message.info(NO_TREES_TO_DOWNLOAD);
    return;
  }

  confirm.update({
    icon: createElement(ExclamationCircleFilled),
    // Two decimals: a small contract's file is a few hundred KB, which one decimal would round away
    content: `Roughly ${megabytes(answer.bytes)} will be downloaded - `
      + `${answer.trees} tree${answer.trees > 1 ? 's' : ''}. Do you want to continue?`,
    okButtonProps: { disabled: false },
  });
}

async function startDownload(filter: any, expectedBytes: number): Promise<void> {
  report({ bytes: 0, expectedBytes });

  try {
    const savedAs = await downloadKML({
      filter,
      onProgress: (bytes) => report({ bytes, expectedBytes }),
    });

    // The app wrote it where the user finds it: two seconds is enough to read the one line
    if (savedAs) {
      message.success(KML_SAVED, SHORT_MESSAGE_SECONDS);
    }
  } catch (error) {
    // `downloadKML` has already told the user about the failure: the overlay only has to go away
  } finally {
    report(null);
  }
}
